import React, { useState, useEffect } from "react";
import { Card, Tabs } from "antd";
import {
  CalendarOutlined,
  BarsOutlined,
  FieldTimeOutlined,
} from "@ant-design/icons";
import ScheduleGridView from "../components/schedule/ScheduleGridView";
import ScheduleTimeline from "../components/schedule/ScheduleTimeline";
import { useAuth } from "../hooks/useAuth";
import ScheduleCalendar from "../components/schedule/ScheduleCalender";
import { usePermissions } from "../context/PermissionContext";
import { PERMISSIONS } from "../utils/permissions";
import { useNavigate } from "react-router-dom";

const StaffSchedulePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState("calendar");
  const { data: user } = useAuth();
  const navigate = useNavigate();

  // Get location ID from logged in user
  const locationId = user?.location?.id;

  // Get user permissions
  const { hasPermission } = usePermissions();
  const canViewSchedules = hasPermission(PERMISSIONS.VIEW_SCHEDULES);
  const canCreateSchedules = hasPermission(PERMISSIONS.CREATE_SCHEDULES);
  const canModifySchedules = hasPermission(PERMISSIONS.MODIFY_SCHEDULES);
  const canDeleteSchedules = hasPermission(PERMISSIONS.DELETE_SCHEDULES);

  // Redirect if user doesn't have permission to view schedules
  useEffect(() => {
    if (!canViewSchedules) {
      // Redirect to unauthorized page with a delay to prevent render issues
      const timeout = setTimeout(() => {
        navigate("/unauthorized");
      }, 100);

      return () => clearTimeout(timeout);
    }
  }, [canViewSchedules, navigate]);

  return (
    <div className="staff-schedule-page">
      <Card>
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">Staff Schedule Management</h1>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          items={[
            {
              key: "calendar",
              label: (
                <span>
                  <CalendarOutlined />
                  Calendar View
                </span>
              ),
              children: (
                <ScheduleCalendar
                  locationId={locationId}
                  canCreateSchedules={canCreateSchedules}
                  canModifySchedules={canModifySchedules}
                  canDeleteSchedules={canDeleteSchedules}
                />
              ),
            },
            {
              key: "list",
              label: (
                <span>
                  <BarsOutlined />
                  List View
                </span>
              ),
              children: (
                <ScheduleGridView
                  locationId={locationId}
                  canCreateSchedules={canCreateSchedules}
                  canModifySchedules={canModifySchedules}
                  canDeleteSchedules={canDeleteSchedules}
                />
              ),
            },
            {
              key: "timeline",
              label: (
                <span>
                  <FieldTimeOutlined />
                  Timeline View
                </span>
              ),
              children: (
                <ScheduleTimeline
                  locationId={locationId}
                  canCreateSchedules={canCreateSchedules}
                  canModifySchedules={canModifySchedules}
                  canDeleteSchedules={canDeleteSchedules}
                />
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default StaffSchedulePage;
